import PropTypes from "prop-types";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { youtubeThumbnailUrl } from "../../../../utils/exercise-video-preview";
import SvgIcon from "../../../SvgIcon";
import { PlayIcon, VideoOffIcon, WarningIcon } from "./VideoPreviewIcons";
import YouTubeInlinePlayer from "./YouTubeInlinePlayer";

const MEDIA_CLASS = "Add-Exercise-videoPreview-media";

/**
 * Left side of the video preview card: empty state, unavailable state, inline player,
 * or the thumbnail with play overlay, source badge and duration.
 */
function VideoPreviewMedia({
  source,
  badgeLabel,
  duration,
  isUnavailable,
  playing,
  uploadSrc,
  onPlay,
  onUploadMetadata,
  onPlayerChange,
  onPlayingChange,
  onPlayerFailed,
}) {
  const { t } = useTranslation("coaching-portal");
  const setVideoElement = useCallback(
    (element) => {
      onPlayerChange(
        element
          ? {
              play: () => {
                element.play()?.catch((error) => {
                  console.warn("[VideoPreviewMedia] play was blocked", error);
                });
              },
              pause: () => element.pause(),
            }
          : null
      );
    },
    [onPlayerChange]
  );
  const isUpload = source.kind === "upload";
  const canPlay = Boolean(isUpload ? uploadSrc : source.playUrl);

  const renderPlayer = () => {
    if (isUpload) {
      return (
        <video
          ref={setVideoElement}
          className="Add-Exercise-videoPreview-player"
          src={uploadSrc}
          controls
          autoPlay
          controlsList="nodownload"
          disablePictureInPicture
          onPlay={() => onPlayingChange(true)}
          onPause={() => onPlayingChange(false)}
          onEnded={() => onPlayingChange(false)}
        >
          <track kind="captions" />
        </video>
      );
    }
    if (source.kind === "youtube") {
      return (
        <YouTubeInlinePlayer
          videoId={source.videoId}
          fallbackUrl={source.playUrl}
          onPlayerChange={onPlayerChange}
          onPlayingChange={onPlayingChange}
          onFailed={onPlayerFailed}
        />
      );
    }
    return (
      <iframe
        className="Add-Exercise-videoPreview-player"
        src={source.playUrl}
        title={t("addExerciseModal.videoPreview.playerTitle")}
        allow="autoplay; encrypted-media; fullscreen"
        allowFullScreen
      />
    );
  };

  if (source.kind === "none") {
    return (
      <div className={`${MEDIA_CLASS} ${MEDIA_CLASS}--empty`}>
        <VideoOffIcon />
        <span>{t("addExerciseModal.videoPreview.noVideoYet")}</span>
      </div>
    );
  }

  if (isUnavailable) {
    return (
      <div className={`${MEDIA_CLASS} ${MEDIA_CLASS}--unavailable`}>
        <WarningIcon />
        <span>{t("addExerciseModal.videoPreview.videoUnavailable")}</span>
      </div>
    );
  }

  if (playing && canPlay) {
    return <div className={MEDIA_CLASS}>{renderPlayer()}</div>;
  }

  return (
    <div className={MEDIA_CLASS}>
      {source.kind === "youtube" ? (
        <img
          className="Add-Exercise-videoPreview-image"
          src={youtubeThumbnailUrl(source.videoId)}
          alt=""
          loading="lazy"
        />
      ) : null}
      {isUpload && uploadSrc ? (
        <video
          className="Add-Exercise-videoPreview-image"
          src={`${uploadSrc}#t=0.1`}
          preload="metadata"
          muted
          playsInline
          onLoadedMetadata={(event) =>
            onUploadMetadata(event.currentTarget.duration)
          }
        />
      ) : null}
      {canPlay ? (
        <button
          type="button"
          className="Add-Exercise-videoPreview-playOverlay"
          aria-label={t("addExerciseModal.videoPreview.play")}
          onClick={onPlay}
        >
          <PlayIcon />
        </button>
      ) : null}
      <span className="Add-Exercise-videoPreview-badge">
        {isUpload ? (
          <SvgIcon
            src="/img/new-icons/Video.svg"
            useCurrentColor
            style={{ width: 16, height: 16 }}
          />
        ) : null}
        {badgeLabel}
      </span>
      {duration ? (
        <span className="Add-Exercise-videoPreview-duration">{duration}</span>
      ) : null}
    </div>
  );
}

VideoPreviewMedia.propTypes = {
  source: PropTypes.shape({
    kind: PropTypes.string.isRequired,
    videoId: PropTypes.string,
    playUrl: PropTypes.string,
  }).isRequired,
  badgeLabel: PropTypes.string,
  duration: PropTypes.string,
  isUnavailable: PropTypes.bool,
  playing: PropTypes.bool,
  uploadSrc: PropTypes.string,
  onPlay: PropTypes.func.isRequired,
  onUploadMetadata: PropTypes.func.isRequired,
  onPlayerChange: PropTypes.func.isRequired,
  onPlayingChange: PropTypes.func.isRequired,
  onPlayerFailed: PropTypes.func.isRequired,
};

export default VideoPreviewMedia;
